/**
 * AnimatedMarquee
 * ───────────────
 * Infinite scrolling marquee with velocity awareness.
 * Accelerates with scroll speed.
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { VVS_MOTION, prefersReducedMotion, getIntensity } from './tokens';

gsap.registerPlugin(ScrollTrigger);

export default function AnimatedMarquee({
  children,
  speed = 50, // pixels per second
  direction = 'left', // 'left' | 'right'
  pauseOnHover = true,
  velocityAware = true,
  className = '',
  style = {},
  ...props
}) {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const tweenRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  
  // Handle scroll velocity
  const handleVelocity = useCallback((normalizedVelocity) => {
    if (!velocityAware || !tweenRef.current) return;
    
    const intensity = getIntensity();
    const speedMultiplier = 1 + normalizedVelocity * 4 * intensity;
    tweenRef.current.timeScale(speedMultiplier);
  }, [velocityAware]);
  
  useEffect(() => {
    if (!containerRef.current || !trackRef.current) return;
    
    // Get track width
    const track = trackRef.current;
    const items = track.children;
    if (items.length === 0) return;
    
    // Clone items for seamless loop
    const totalWidth = track.scrollWidth;
    const cloneCount = Math.ceil(window.innerWidth / totalWidth) + 1;
    
    // Clear existing clones
    while (track.children.length > items.length) {
      track.removeChild(track.lastChild);
    }
    
    // Add clones
    for (let i = 0; i < cloneCount; i++) {
      Array.from(items).forEach(item => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
    }
    
    if (prefersReducedMotion()) return;
    
    const ctx = gsap.context(() => {
      // Calculate duration based on speed
      const duration = totalWidth / speed;
      const xPercent = direction === 'left' ? -100 : 100;
      
      tweenRef.current = gsap.to(track, {
        x: direction === 'left' ? -totalWidth : totalWidth,
        duration,
        ease: "none",
        repeat: -1,
        modifiers: {
          x: gsap.utils.unitize(x => parseFloat(x) % totalWidth),
        },
      });
      
      // Velocity awareness
      if (velocityAware) {
        ScrollTrigger.addEventListener("scrollStart", () => {});
        ScrollTrigger.addEventListener("scrollEnd", () => {
          gsap.to(tweenRef.current, {
            timeScale: 1,
            duration: 0.8,
            ease: VVS_MOTION.organic,
          });
        });
      }
    }, containerRef);
    
    return () => {
      ctx.revert();
    };
  }, [speed, direction, velocityAware]);
  
  // Scroll velocity tracking
  useEffect(() => {
    if (!velocityAware || prefersReducedMotion()) return;
    
    let lastScrollY = window.scrollY;
    let lastTime = Date.now();
    let rafId;
    
    const updateVelocity = () => {
      const currentScrollY = window.scrollY;
      const currentTime = Date.now();
      const deltaTime = currentTime - lastTime;
      
      if (deltaTime > 0) {
        const deltaScroll = currentScrollY - lastScrollY;
        const velocity = Math.abs(deltaScroll / deltaTime) * 1000;
        const normalizedVelocity = Math.min(velocity / 3000, 1);
        handleVelocity(normalizedVelocity);
      }
      
      lastScrollY = currentScrollY;
      lastTime = currentTime;
      rafId = requestAnimationFrame(updateVelocity);
    };
    
    rafId = requestAnimationFrame(updateVelocity);
    
    return () => cancelAnimationFrame(rafId);
  }, [velocityAware, handleVelocity]);
  
  // Pause on hover
  useEffect(() => {
    if (!pauseOnHover || !tweenRef.current) return;
    
    if (isHovered) {
      gsap.to(tweenRef.current, {
        timeScale: 0,
        duration: 0.5,
        ease: VVS_MOTION.exit,
      });
    } else {
      gsap.to(tweenRef.current, {
        timeScale: 1,
        duration: 0.8,
        ease: VVS_MOTION.organic,
      });
    }
  }, [isHovered, pauseOnHover]);
  
  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        ...style,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      <div
        ref={trackRef}
        style={{
          display: 'inline-flex',
          willChange: 'transform',
        }}
      >
        {children}
      </div>
    </div>
  );
}
